import { apiClient } from "./client";

export type PushConfig = {
  enabled: boolean;
  public_key?: string | null;
};

export async function getPushConfig(): Promise<PushConfig> {
  const { data } = await apiClient.get<PushConfig>("/api/v1/me/push/config");
  return data;
}

export async function subscribePush(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  await apiClient.post("/api/v1/me/push/subscriptions", {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });
}

export async function unsubscribePush(endpoint: string): Promise<void> {
  await apiClient.post("/api/v1/me/push/unsubscribe", { endpoint });
}

export async function sendTestPush(): Promise<{ delivered: number }> {
  const { data } = await apiClient.post<{ delivered: number }>("/api/v1/me/push/test");
  return data;
}

/** VAPID keys travel as base64url; the browser wants raw bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushSupport =
  | { supported: true }
  | { supported: false; reason: "no-service-worker" | "no-push" | "ios-needs-install" };

/**
 * Why push may be unavailable here.
 *
 * iOS is the tricky one: Safari only exposes PushManager to a PWA that was
 * added to the Home Screen (16.4+). In a normal Safari tab the API is absent,
 * and no amount of prompting will help — the user has to install first.
 */
export function detectPushSupport(): PushSupport {
  if (!("serviceWorker" in navigator)) return { supported: false, reason: "no-service-worker" };
  // Read through globalThis: `"PushManager" in window` narrows `window` to
  // `never` in the else-branch, which then rejects any further access.
  const hasPushManager = "PushManager" in globalThis;
  if (!hasPushManager) {
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (isIOS && !standalone) return { supported: false, reason: "ios-needs-install" };
    return { supported: false, reason: "no-push" };
  }
  return { supported: true };
}
